import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import callOysterApi from '../../utils/call-oyster-api.js';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import ConfirmationModal from '../../components/common/ConfirmationModal.jsx';
import TasksTable from '../../components/tasks/TasksTable.jsx';
import TaskDetailsModal from '../../components/tasks/TaskDetailsModal.jsx';

const UserTasksView = ({
    userEmail,
    userName,
    quarter,
    weekRange,
    weekLabel,
    hasManagementRights,
    isItLeadership,
    currentUserEmail,
    currentUserTeam,
    allTasks,
    loadingTasks,
    refreshTasks,
    showTasks,
    onTaskStatusUpdated
}) => {
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [taskToComplete, setTaskToComplete] = useState(null);
    const [selectedTask, setSelectedTask] = useState(null);

    const isInSelectedWeek = useMemo(() => {
        if (!weekRange) return () => true;
        const start = Date.parse(`${weekRange.start}T00:00:00Z`);
        const end = Date.parse(`${weekRange.end}T23:59:59Z`);

        const inRange = (value) => {
            if (!value) return false;
            const parsed = Date.parse(`${value}T00:00:00Z`);
            return !Number.isNaN(parsed) && parsed >= start && parsed <= end;
        };

        return (task) => {
            const completedDate =
                task.completed_date ||
                (task.completed_at ? task.completed_at.split('T')[0] : null);
            return inRange(task.due_date) || inRange(completedDate);
        };
    }, [weekRange]);

    const userTasks = useMemo(() => {
        if (!allTasks) return [];
        const scopedTasks = allTasks.filter(
            (t) => t.assignee_email?.toLowerCase() === userEmail?.toLowerCase()
        );
        if (!weekRange) return scopedTasks;
        return scopedTasks.filter(isInSelectedWeek);
    }, [allTasks, userEmail, weekRange, isInSelectedWeek]);

    // Separate subtasks from top-level items
    const topLevel = useMemo(
        () => userTasks.filter((t) => !t.parent_task_id),
        [userTasks]
    );
    const subtaskMap = useMemo(() => {
        const map = new Map();
        userTasks
            .filter((t) => t.parent_task_id)
            .forEach((t) => {
                const existing = map.get(t.parent_task_id) || [];
                existing.push(t);
                map.set(t.parent_task_id, existing);
            });
        return map;
    }, [userTasks]);

    // Split top-level by item_type
    const rocks = useMemo(
        () => topLevel.filter((t) => t.item_type === 'rock'),
        [topLevel]
    );
    const tasks = useMemo(
        () => topLevel.filter((t) => t.item_type !== 'rock'),
        [topLevel]
    );

    const getStats = (items) => ({
        total: items.length,
        completed: items.filter((t) => t.status === 'completed').length,
        pending: items.filter((t) => t.status === 'needsAction').length,
        overdue: items.filter(
            (t) => t.status === 'needsAction' && t.sla?.is_overdue
        ).length
    });

    const rockStats = useMemo(() => getStats(rocks), [rocks]);
    const taskStats = useMemo(() => getStats(tasks), [tasks]);

    const handleMarkComplete = (task) => {
        setTaskToComplete(task);
        setIsConfirmModalOpen(true);
    };

    const handleRowClick = (task) => {
        if (task.parent_task_id) {
            // Subtask clicked — open the parent's details modal
            const parent = allTasks?.find(
                (t) => t.task_id === task.parent_task_id
            );
            setSelectedTask(parent || task);
        } else {
            setSelectedTask(task);
        }
    };

    const confirmCompleteTask = async () => {
        if (!taskToComplete) return;
        try {
            const response = await callOysterApi(
                `/users/${encodeURIComponent(
                    taskToComplete.assignee_email
                )}/todos/${encodeURIComponent(taskToComplete.id)}/status`,
                'PATCH',
                { status: 'completed' }
            );
            if (response.ok) {
                toast.success('Marked as complete!');
                if (onTaskStatusUpdated) {
                    onTaskStatusUpdated(taskToComplete.id, 'completed');
                }
                refreshTasks();
                // Optionally update local state if it's the currently opened modal
                if (selectedTask && selectedTask.id === taskToComplete.id) {
                    setSelectedTask({ ...selectedTask, status: 'completed' });
                }
            } else {
                toast.error('Failed to update');
            }
        } catch (error) {
            console.error('Error updating task:', error);
        } finally {
            setTaskToComplete(null);
            setIsConfirmModalOpen(false);
        }
    };

    if (loadingTasks) return <LoadingSpinner />;

    const renderStatsRow = (stats, label) => (
        <div>
            <h3 className="section-label"> {label} </h3>
            <div className="stats-grid compact-stats">
                <div className="stat-card">
                    <div className="stat-title">Total</div>
                    <div className="stat-value">{stats.total}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-title">Pending</div>
                    <div className="stat-value">{stats.pending}</div>
                </div>
                <div className="stat-card">
                    <div className="stat-title">Completed</div>
                    <div className="stat-value text-green">
                        {stats.completed}
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-title">Overdue</div>
                    <div className="stat-value text-red">{stats.overdue}</div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="user-tasks-view">
            {/* Completion Modal */}
            {isConfirmModalOpen && (
                <ConfirmationModal
                    title="Complete Item"
                    message={`Are you sure you want to mark "${taskToComplete?.title}" as complete?`}
                    onConfirm={confirmCompleteTask}
                    setShowModal={setIsConfirmModalOpen}
                />
            )}

            {/* Jira-Style Task Details Modal */}
            {selectedTask && (
                <TaskDetailsModal
                    task={selectedTask}
                    subtasks={subtaskMap.get(selectedTask.task_id) || []}
                    onClose={() => setSelectedTask(null)}
                    onSubtasksChanged={refreshTasks}
                    onTaskStatusUpdated={onTaskStatusUpdated}
                    currentUserEmail={currentUserEmail}
                    currentUserTeam={currentUserTeam}
                    isItLeadership={isItLeadership}
                    hasManagementRights={hasManagementRights}
                />
            )}

            {renderStatsRow(rockStats, 'Missions')}
            <TasksTable
                items={rocks}
                subtaskMap={subtaskMap}
                label="Mission"
                emptyMessage={`No missions found for ${userName} in ${quarter}${
                    weekRange ? ` (${weekLabel})` : ''
                }.`}
                onRowClick={handleRowClick}
                onCompleteAction={handleMarkComplete}
                currentUserEmail={currentUserEmail}
                currentUserTeam={currentUserTeam}
                isItLeadership={isItLeadership}
                hasManagementRights={hasManagementRights}
            />

            <div style={{ marginTop: '2rem' }} />

            {showTasks && (
                <>
                    {renderStatsRow(taskStats, 'Tasks')}
                    <TasksTable
                        items={tasks}
                        subtaskMap={subtaskMap}
                        label="Task"
                        emptyMessage={`No tasks found for ${userName} in ${quarter}${
                            weekRange ? ` (${weekLabel})` : ''
                        }.`}
                        onRowClick={handleRowClick}
                        onCompleteAction={handleMarkComplete}
                        currentUserEmail={currentUserEmail}
                        currentUserTeam={currentUserTeam}
                        isItLeadership={isItLeadership}
                        hasManagementRights={hasManagementRights}
                    />
                </>
            )}
        </div>
    );
};

UserTasksView.propTypes = {
    userEmail: PropTypes.string.isRequired,
    userName: PropTypes.string.isRequired,
    quarter: PropTypes.string.isRequired,
    weekRange: PropTypes.shape({
        start: PropTypes.string.isRequired,
        end: PropTypes.string.isRequired
    }),
    weekLabel: PropTypes.string,
    hasManagementRights: PropTypes.bool.isRequired,
    isItLeadership: PropTypes.bool,
    currentUserEmail: PropTypes.string.isRequired,
    currentUserTeam: PropTypes.string,
    allTasks: PropTypes.array,
    loadingTasks: PropTypes.bool.isRequired,
    refreshTasks: PropTypes.func.isRequired,
    showTasks: PropTypes.bool,
    onTaskStatusUpdated: PropTypes.func
};

export default UserTasksView;

// end of file
